import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Shield, Crosshair, Crown, Star } from "lucide-react";
import { TIERS } from "@/data/content";
import { openWhatsApp } from "@/lib/api";
import { SectionEyebrow } from "@/components/Marquee";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ICONS = { Shield, Crosshair, Crown, Star };
const HAS_PAGE = ["drona", "arjuna", "bhishma"];

export default function Tiers() {
  const [view, setView] = useState("cards");
  return (
    <section id="tiers" className="relative py-20 md:py-28 bg-void" data-testid="tiers-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <SectionEyebrow n="Ch. II" testId="tiers-eyebrow">The Alliance</SectionEyebrow>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.8, 0, 0.2, 1] }}
              className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl"
            >
              Choose your level of <span className="italic gradient-text">Saarthi.</span>
            </motion.h2>
          </div>
          <Tabs value={view} onValueChange={setView} className="w-full md:w-auto" data-testid="tiers-view-toggle">
            <TabsList className="bg-surface border border-white/10 p-1 rounded-full">
              <TabsTrigger value="cards" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="view-cards">
                Cards
              </TabsTrigger>
              <TabsTrigger value="table" className="rounded-full px-5 text-sm data-[state=active]:gradient-bg data-[state=active]:text-white" data-testid="view-table">
                Compare
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <AnimatePresence mode="wait">
          {view === "cards" ? (
            <motion.div
              key="cards"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4"
            >
              {TIERS.map((t, i) => {
                const Icon = ICONS[t.icon] || Star;
                return (
                  <motion.article
                    key={t.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, delay: i * 0.08 }}
                    className={`relative rounded-3xl p-8 md:p-9 overflow-hidden border ${t.highlight ? "gradient-border bg-[#141323]" : "border-white/5 bg-surface"} ${t.comingSoon ? "opacity-80" : ""} group`}
                    data-testid={`tier-card-${t.id}`}
                  >
                    {t.badge && (
                      <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full gradient-bg text-white font-semibold" data-testid={`tier-${t.id}-badge`}>
                        {t.badge}
                      </div>
                    )}
                    <div className="w-12 h-12 rounded-2xl grid place-items-center bg-white/5 border border-white/10 mb-6" style={{ color: t.color }}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="eyebrow" style={{ color: t.color }}>{t.category}</div>
                    <h3 className="mt-2 font-serif text-4xl text-white leading-none">{t.name}</h3>
                    <p className="mt-4 text-silver/70 leading-relaxed text-sm italic">{t.tagline}</p>
                    {t.forWhen && (
                      <p className="mt-4 text-sm text-white/80 leading-snug border-l-2 pl-3" style={{ borderColor: t.color }}>
                        {t.forWhen}
                      </p>
                    )}
                    <div className="mt-6">
                      <span className="font-serif text-2xl md:text-3xl text-white">{t.priceLabel}</span>
                    </div>
                    <ul className="mt-6 space-y-2.5">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-silver/80">
                          <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.color }} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => !t.comingSoon && openWhatsApp({ source: "tier_card", tier: t.name })}
                      disabled={t.comingSoon}
                      className={`mt-8 w-full py-3.5 rounded-full text-sm font-medium transition-transform duration-200 ${t.comingSoon ? "border border-white/10 text-silver/50 cursor-not-allowed" : `hover:scale-[1.02] ${t.highlight ? "gradient-bg text-white shadow-[0_15px_40px_rgba(139,92,246,0.4)]" : "border border-white/15 text-white hover:bg-white/5"}`}`}
                      data-testid={`tier-${t.id}-cta`}
                    >
                      {t.cta} {!t.comingSoon && "→"}
                    </button>
                    {HAS_PAGE.includes(t.id) && (
                      <Link
                        to={`/${t.id}`}
                        className="mt-3 block text-center text-xs text-silver/50 hover:text-white transition-colors"
                        data-testid={`tier-${t.id}-learn-more`}
                      >
                        Learn more about {t.name} →
                      </Link>
                    )}
                  </motion.article>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="table"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="mt-16 rounded-3xl border border-white/10 bg-surface overflow-hidden"
              data-testid="tiers-comparison-table"
            >
              <div className="grid grid-cols-5 text-sm">
                <div className="p-5 md:p-6 border-r border-white/5 text-silver/50 uppercase tracking-widest text-xs">Feature</div>
                {TIERS.map((t) => (
                  <div key={t.id} className="p-5 md:p-6 border-r border-white/5 last:border-r-0">
                    <div className="font-serif text-xl text-white">{t.name}</div>
                    <div className="text-xs text-silver/50">{t.priceLabel}</div>
                  </div>
                ))}
              </div>
              {[
                "Car & variant advisory",
                "Dealer price negotiation",
                "Accessories & insurance advisory",
                "Remote PDI (60+ point portal)",
                "Dedicated personal advisor",
                "Physical presence & in-person PDI",
                "Loan & RC follow-up support",
              ].map((f, i) => {
                const truth = [
                  [1, 1, 1, 1],
                  [0, 1, 1, 1],
                  [0, 1, 1, 1],
                  [0, 0, 1, 1],
                  [0, 0, 1, 1],
                  [0, 0, 0, 1],
                  [0, 0, 0, 1],
                ][i];
                return (
                  <div key={f} className="grid grid-cols-5 text-sm border-t border-white/5">
                    <div className="p-4 md:p-5 border-r border-white/5 text-silver/80">{f}</div>
                    {truth.map((v, j) => (
                      <div key={j} className="p-4 md:p-5 border-r border-white/5 last:border-r-0">
                        {v ? <Check className="w-4 h-4 text-[#4FC3F7]" /> : <span className="text-white/20">—</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
              <div className="grid grid-cols-5 border-t border-white/5">
                <div className="p-4 md:p-5 border-r border-white/5" />
                {TIERS.map((t) => (
                  <div key={t.id} className="p-4 md:p-5 border-r border-white/5 last:border-r-0">
                    <button
                      onClick={() => !t.comingSoon && openWhatsApp({ source: "tier_table", tier: t.name })}
                      disabled={t.comingSoon}
                      className={`text-xs px-4 py-2 rounded-full transition-transform ${t.comingSoon ? "border border-white/10 text-silver/50 cursor-not-allowed" : "gradient-bg text-white hover:scale-[1.03]"}`}
                      data-testid={`table-tier-${t.id}-cta`}
                    >
                      {t.comingSoon ? "Soon" : "Choose"}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-14 rounded-3xl border border-white/10 bg-surface p-8 md:p-10" data-testid="tiers-commitment">
          <div className="eyebrow gradient-text text-center">No savings. No performance fee.</div>
          <p className="mt-4 font-serif text-xl md:text-2xl text-white/90 max-w-2xl mx-auto leading-relaxed text-center">
            You pay a fixed engagement fee to begin. Our performance fee applies only when we actually save you money.
          </p>
          <p className="mt-6 font-serif text-2xl md:text-3xl text-white text-center">
            Your Savings <span className="text-silver/50 mx-1">×</span> 20%
            <span className="text-silver/50 mx-2">=</span>
            <span className="italic gradient-text">Our Success Fee</span>
          </p>
        </div>

        <p className="mt-10 text-center italic font-serif text-2xl md:text-3xl text-white/70">
          "Every great victory needs the right <span className="gradient-text">alliance.</span> Choose yours."
        </p>
      </div>
    </section>
  );
}
